import type { FunctionComponent } from 'react'
import type { FieldSize } from './field-size'
import type { BuiltInValidatorProps } from './FormBuiltInValidators'
import type { FormChangeHandler, FormFlagHandler, FormSubmitHandler } from './FormContext'
import type { FieldValidator } from './FormValidators'

import { useMemo, useRef } from 'react'

import { useRafEffect } from '../hooks/useRafEffect'
import { assert } from '../utilities/assert'
import { useBuiltInValidatorProps } from './FormBuiltInValidators'
import { useFormContext, useFormReducer } from './FormContext'

export type FieldHocProps<V, P = {}> = {
  name?: string
  value?: V
  size?: FieldSize
  disabled?: boolean
  flags?: string[]
  erroringFlags?: string[]
  validate?: FieldValidator<V> | FieldValidator<V>[]
  onChange?: (value: V) => void
  onSubmit?: () => void
  onFlag?: (flags: string[], showErrors: boolean) => void
} & BuiltInValidatorProps<V> &
  P

export type InternalFieldHocProps<V, P = {}> = {
  value?: V
  size?: FieldSize
  disabled: boolean
  hasError: boolean
  hasParentForm: boolean
  onChange: (value: V) => void
  onSubmit: () => void
} & BuiltInValidatorProps<V> &
  P

export const fieldHoc = <V, P extends Record<string, any>>(
  Field: FunctionComponent<InternalFieldHocProps<V, P>>,
) => {
  const FieldHoc = (props: FieldHocProps<V, P>) => {
    const {
      name,
      value,
      flags: providedFlags,
      disabled,
      erroringFlags,
      validate: fieldValidatorOrValidators,
      onChange: fieldChangeHandler,
      onFlag: fieldFlagHandler,
      onSubmit: fieldSubmitHandler,
    } = props

    const isStandalone = value !== undefined

    const validatorProps = useBuiltInValidatorProps(props)
    const nameOrDefaultName = name ?? 'field'

    const formChangeHandlerRef = useRef<FormChangeHandler<any>>(() => {})
    const formFlagHandlerRef = useRef<FormFlagHandler>(() => {})
    const formSubmitHandlerRef = useRef<FormSubmitHandler>(() => {})

    formChangeHandlerRef.current = (formValue) => fieldChangeHandler?.(formValue[nameOrDefaultName])
    formFlagHandlerRef.current = (formFlags, showErrors) =>
      fieldFlagHandler?.(formFlags[nameOrDefaultName] ?? [], showErrors)
    formSubmitHandlerRef.current = () => fieldSubmitHandler?.()

    // When value is provided, the field manages itself — create a virtual form.
    // When value is absent, use the form context (joins parent form or creates virtual form).
    const [formState, dispatch] = isStandalone
      ? useFormReducer(undefined)
      : useFormContext({
          value: useMemo(() => ({ field: value }), [value]),
          providedFlags: useMemo(() => ({ field: providedFlags ?? [] }), [providedFlags]),
          erroredFieldFlags: useMemo(() => ({ field: erroringFlags ?? [] }), [erroringFlags]),
          formChangeHandlerRef,
          formFlagHandlerRef,
          formSubmitHandlerRef,
        })

    const participatesInForm = formState.belongsToForm && !isStandalone

    if (participatesInForm)
      assert(name, 'Field must have a name when used within a Form component.')

    const fieldValue = participatesInForm ? formState.value[nameOrDefaultName] : value

    const fieldValidatorsMemo = () =>
      typeof fieldValidatorOrValidators === 'function'
        ? [fieldValidatorOrValidators]
        : fieldValidatorOrValidators
    const fieldValidators = useMemo(fieldValidatorsMemo, [fieldValidatorOrValidators])

    const canShowErrored = formState.hasAttemptedSubmit || !formState.showErrorFlagsAfterSubmit
    const provided = formState.providedFlags[nameOrDefaultName] ?? []
    const hasProvidedError =
      provided.length > 0 &&
      provided.some(
        (f) =>
          (formState.erroringFieldFlags[nameOrDefaultName]?.[f] ?? 0) > 0 ||
          !!formState.erroringFormFlags[nameOrDefaultName]?.includes(f),
      )
    const hasError =
      participatesInForm &&
      canShowErrored &&
      (!!formState.erroredFormFlags[nameOrDefaultName]?.length ||
        !!formState.erroredFieldFlags[nameOrDefaultName]?.length ||
        hasProvidedError)

    const validateEffect = () => {
      if (!participatesInForm) return
      dispatch({
        type: 'runFieldValidator',
        name: nameOrDefaultName,
        fieldValidators: fieldValidators ?? [],
        validatorProps,
      })
    }

    useRafEffect(validateEffect, [
      dispatch,
      fieldValidators,
      fieldValue,
      nameOrDefaultName,
      participatesInForm,
      validatorProps,
    ])

    const handleChange = (value: any) => {
      if (participatesInForm) {
        dispatch({ type: 'setFieldValue', name: nameOrDefaultName, value })
      } else {
        fieldChangeHandler?.(value)
      }
    }

    const trySubmit = () => {
      if (participatesInForm) {
        dispatch({ type: 'trySubmit' })
      } else {
        fieldSubmitHandler?.()
      }
    }

    return (
      <Field
        {...props}
        value={fieldValue}
        disabled={!!disabled}
        hasError={hasError}
        hasParentForm={participatesInForm}
        onChange={handleChange}
        onSubmit={trySubmit}
      />
    )
  }

  return FieldHoc
}
